import type { Form, FormField, FormPage, FieldType } from '@/types/forms';
import { PAGE_EXCLUDED_FIELD_TYPES } from '@/types/forms';

/**
 * Tipos que ficam FORA da resolução de páginas.
 *
 * Menor que PAGE_EXCLUDED_FIELD_TYPES de propósito: `redirect_url` continua sem
 * poder ser agrupado numa página multi-pergunta (isso é PAGE_EXCLUDED, usado pelo
 * builder), mas PRECISA de página implícita própria aqui — é um slide navegável e
 * alvo legítimo de desvio. Sem página, `buildNextPageMap` não conseguia resolver o
 * salto e a tela nunca era alcançada.
 */
const PAGE_RESOLUTION_EXCLUDED_TYPES: FieldType[] = ['welcome_screen', 'thank_you_screen'];

// =====================================================
// MULTI-QUESTION PAGE HELPERS (Epic 5 - Story 5.1)
// =====================================================

export interface ResolvedPage {
  page: FormPage;
  fields: FormField[];
}

/**
 * Resolve form fields into pages for the Typeform renderer.
 *
 * - If form.pages exists: groups fields by pageId, respects page.order
 * - If form.pages is missing: each field becomes its own implicit page (backward compat)
 * - Fields without pageId become individual pages
 * - welcome_screen e thank_you_screen ficam fora das páginas — são renderizadas
 *   antes do fluxo e depois do envio. redirect_url ganha página própria (ver
 *   PAGE_RESOLUTION_EXCLUDED_TYPES acima).
 *
 * **Field order:** Fields within a page are ordered by their position in
 * `form.fields[]`. This is a design decision — the form.fields array is the
 * canonical source of field ordering across the entire application.
 *
 * **Empty pages:** Pages defined in `form.pages` but with no assigned fields
 * are intentionally excluded from the output. The renderer has no use for
 * empty pages. The builder should use `form.pages` directly when it needs
 * to display or manage empty page containers (Story 5.2).
 */
export function resolvePages(form: Form): ResolvedPage[] {
  // Filter out page-excluded field types
  const pageableFields = form.fields.filter(
    (f) => !PAGE_RESOLUTION_EXCLUDED_TYPES.includes(f.type)
  );

  // No pages defined → each field is its own implicit page (backward compat)
  if (!form.pages || form.pages.length === 0) {
    return pageableFields.map((field, index) => ({
      page: {
        id: `__implicit_${field.id}`,
        columns: 1 as const,
        order: index,
      },
      fields: [field],
    }));
  }

  // Group fields by pageId
  const pageMap = new Map<string, FormField[]>();
  const orphanFields: FormField[] = [];

  for (const field of pageableFields) {
    if (field.pageId && pageMap.has(field.pageId)) {
      pageMap.get(field.pageId)!.push(field);
    } else if (field.pageId) {
      // Check if the page actually exists
      const pageExists = form.pages.some((p) => p.id === field.pageId);
      if (pageExists) {
        pageMap.set(field.pageId, [field]);
      } else {
        orphanFields.push(field);
      }
    } else {
      orphanFields.push(field);
    }
  }

  // Build resolved pages from defined pages (sorted by order)
  const sortedPages = [...form.pages].sort((a, b) => a.order - b.order);
  const resolved: ResolvedPage[] = [];

  for (const page of sortedPages) {
    const fields = pageMap.get(page.id) || [];
    if (fields.length > 0) {
      resolved.push({ page, fields });
    }
  }

  // Orphan fields (no pageId or invalid pageId) become individual pages
  let maxOrder = sortedPages.length > 0
    ? Math.max(...sortedPages.map((p) => p.order))
    : -1;

  for (const field of orphanFields) {
    maxOrder++;
    resolved.push({
      page: {
        id: `__implicit_${field.id}`,
        columns: 1 as const,
        order: maxOrder,
      },
      fields: [field],
    });
  }

  return resolved;
}

/**
 * Get page-excluded fields (welcome_screen, thank_you_screen, redirect_url)
 * from a form. These are rendered separately by the Typeform renderer.
 */
export function getExcludedFields(form: Form): {
  welcomeFields: FormField[];
  endFields: FormField[];
} {
  const welcomeFields: FormField[] = [];
  const endFields: FormField[] = [];

  for (const field of form.fields) {
    if (field.type === 'welcome_screen') {
      welcomeFields.push(field);
    } else if (field.type === 'thank_you_screen' || field.type === 'redirect_url') {
      endFields.push(field);
    }
  }

  return { welcomeFields, endFields };
}

/**
 * Create a new FormPage with a unique ID and sensible defaults.
 */
export function createPage(title?: string): FormPage {
  return {
    id: crypto.randomUUID(),
    title,
    columns: 1,
    order: 0, // Caller should set the correct order
  };
}

/**
 * Check if a field type can be added to a page.
 * welcome_screen, thank_you_screen, and redirect_url cannot be in pages.
 */
export function canFieldBeInPage(fieldType: FieldType): boolean {
  return !PAGE_EXCLUDED_FIELD_TYPES.includes(fieldType);
}
